import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { formatMoney } from "../utils/formatMoney";

const RequestPayoutDialog = ({ open, onClose, summary, onSubmit, saving }) => {
  const [form, setForm] = useState({
    amount: "",
    bankAccountInfo: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const payableAmount = Number(summary?.payableAmount ?? 0);
  const minimumAmount = summary?.minimumPayoutAmount
    ? Number(summary.minimumPayoutAmount)
    : null;

  useEffect(() => {
    if (!open) return;
    setForm({ amount: "", bankAccountInfo: "", description: "" });
    setErrors({});
    setSubmitError(null);
  }, [open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    if (submitError) setSubmitError(null);
  };

  const validate = () => {
    const next = {};
    const amount = Number(form.amount);
    if (!amount || amount <= 0) next.amount = "مبلغ باید بزرگ‌تر از صفر باشد.";
    if (amount > payableAmount) {
      next.amount = `مبلغ نمی‌تواند بیشتر از ${formatMoney(payableAmount)} باشد.`;
    }
    if (minimumAmount != null && amount < minimumAmount) {
      next.amount = `حداقل مبلغ تسویه ${formatMoney(minimumAmount)} است.`;
    }
    if (!form.bankAccountInfo?.trim()) {
      next.bankAccountInfo = "اطلاعات حساب بانکی الزامی است.";
    }
    return next;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }
    const result = await onSubmit({
      amount: Number(form.amount),
      bankAccountInfo: form.bankAccountInfo.trim(),
      description: form.description?.trim() || null,
    });
    if (result?.error) setSubmitError(result.error);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>درخواست تسویه حساب</DialogTitle>
      <DialogContent dividers>
        {submitError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}
        <Alert severity="info" sx={{ mb: 2 }}>
          مبلغ قابل تسویه: {formatMoney(payableAmount)}
          {minimumAmount != null ? ` — حداقل: ${formatMoney(minimumAmount)}` : ""}
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="مبلغ درخواستی"
              type="number"
              value={form.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
              fullWidth
              required
              error={Boolean(errors.amount)}
              helperText={errors.amount}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="اطلاعات حساب بانکی"
              value={form.bankAccountInfo}
              onChange={(e) => handleChange("bankAccountInfo", e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
              error={Boolean(errors.bankAccountInfo)}
              helperText={errors.bankAccountInfo}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="توضیحات"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          انصراف
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "در حال ارسال..." : "ثبت درخواست"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestPayoutDialog;
